import { gql } from "@apollo/client";
import { Button } from "@mui/material";
import Field from "controls/Field";
import FormDialog, { FormDialogButtons, FormDialogColumn, FormDialogColumns } from "controls/FormDialog";
import FormSection from "controls/FormSection";
import FormText from "controls/FormText";
import Message from "controls/Message";
import b64ToBlob from "helpers/b64ToBlob";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import useIsAdmin from "hooks/useIsAdmin";
import { progressBar } from "hooks/useProgressBar";
import NumberInput from "inputs/NumberInput";
import PolygonInput from "inputs/PolygonInput";
import SelectInput from "inputs/SelectInput";
import SwitchInput from "inputs/SwitchInput";
import { Pencil } from "mdi-material-ui";
import React, { useLayoutEffect, useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";
import { useAsyncMemo } from "use-async-memo";

import SphereCameraPreview from "./SphereCameraPreview";

SurfaceEncodingConfigDialog.useButtonProps = () => {
  const isAdmin = useIsAdmin();
  return {
    hidden: !isAdmin,
    content: "Encoding Config",
    icon: <Pencil />,
  };
};

export default function SurfaceEncodingConfigDialog({ surfaceId, onSubmit, onClose }) {
  const [data, dataMeta] = useData(
    gql`
      query SurfaceEncodingConfigDialog($surfaceId: ID!) {
        surface(id: $surfaceId) {
          id
          camera {
            id
          }
          motionArea
          projectionShape
          projectionInterpolation
          fovHorizontalDegrees
          fovVerticalDegrees
          pitchDegrees
          rollDegrees
          sphereCameraPitchDegrees
          sphereCameraFovHorizontalDegrees
          courtCorners
          maxZoom
          macroFrameVerticalBalance
          advertisingBillboardHeight
          advertisingBillboardAngle
          advertisingBillboardScrollSpeed
          advertisingBillboardAlwaysForeground
          advertisingBillboardBottomEdge
          advertisingCentreCourt
          netLeftPolygon
          netRightPolygon
          courtLength
          courtWidth
          cameraHeight
          cameraDistance
        }
      }
    `,
    { surfaceId },
  );
  const surfaceEncodingConfigUpdate = useActionFragment("surfaceEncodingConfigUpdate");

  const [video, videoSet] = useState(null);
  const [projectionShape, projectionShapeSet] = useState(null);
  const [fovHorizontalDegrees, fovHorizontalDegreesSet] = useState(null);
  const [fovVerticalDegrees, fovVerticalDegreesSet] = useState(null);
  const [pitchDegrees, pitchDegreesSet] = useState(null);
  const [rollDegrees, rollDegreesSet] = useState(null);
  const [sphereCameraPitchDegrees, sphereCameraPitchDegreesSet] = useState(null);
  const [sphereCameraFovHorizontalDegrees, sphereCameraFovHorizontalDegreesSet] = useState(null);

  useLayoutEffect(() => {
    if (data && !dataMeta.loading) {
      projectionShapeSet(data.surface.projectionShape);
      fovHorizontalDegreesSet(data.surface.fovHorizontalDegrees);
      fovVerticalDegreesSet(data.surface.fovVerticalDegrees);
      pitchDegreesSet(data.surface.pitchDegrees);
      rollDegreesSet(data.surface.rollDegrees);
      sphereCameraPitchDegreesSet(data.surface.sphereCameraPitchDegrees);
      sphereCameraFovHorizontalDegreesSet(data.surface.sphereCameraFovHorizontalDegrees);
    }
  }, [dataMeta.loading]);
  const sphereCameraConfigPresent =
    fovHorizontalDegrees !== null &&
    fovVerticalDegrees !== null &&
    pitchDegrees !== null &&
    rollDegrees !== null &&
    sphereCameraPitchDegrees !== null;

  const surfacePreviewFilterChain = useActionFragment("surfacePreviewFilterChain", "jpgImageBase64");

  const filterChainPreviewUrl = useAsyncMemo(
    () =>
      progressBar(async () => {
        const result = await surfacePreviewFilterChain({ input: { surfaceId, videoId: video?.id } });
        const jpgImageBase64 = result.surfacePreviewFilterChain.jpgImageBase64;

        const blob = b64ToBlob(jpgImageBase64);
        const url = URL.createObjectURL(blob);
        return url;
      }).catch(() => null),
    [surfaceId, video?.id],
  );

  return (
    <FormDialog
      header="Surface Encoding Config"
      loading={dataMeta.loading}
      onClose={onClose}
      maxWidth="lg"
      onSubmit={async (formData) => {
        await surfaceEncodingConfigUpdate({ input: { surfaceId, ...formData } });
        await onSubmit?.();
      }}
    >
      <FormText uiStringKey="readmin.surface.encoding_config" />
      <Field
        label="Motion Area"
        name="motionArea"
        defaultValue={data?.surface.motionArea}
        input={<PolygonInput backgroundUrl={filterChainPreviewUrl} stroke fill />}
        helperTextUiStringKey="readmin.surface.fields.motion_area"
      />
      <Field
        label="Projection Shape"
        name="projectionShape"
        value={projectionShape}
        onChange={projectionShapeSet}
        input={
          <SelectInput
            options={[
              ["Plane", "plane", { description: "(default) for bullet cameras." }],
              ["Sphere", "sphere", { description: "for 180 or dome cameras." }],
            ]}
          />
        }
        helperTextUiStringKey="readmin.surface.fields.projection_shape"
      />
      <Field
        name="projectionInterpolation"
        label="Projection Interpolation"
        defaultValue={data?.surface.projectionInterpolation}
        input={<SelectInput options={["nearest", "bilinear", "area"]} />}
        helperText={
          <>
            Defaults to nearest neighbor for bullet cameras, and bilinear for 180 cameras.
            <br />
            Bilinear may be required for some bullet cameras to fix visual artifacts, but in most cases nearest neighbor
            is sufficient.
            <br />
            Area is slower, but further reduces moire effects compared to bilinear.
          </>
        }
        helperTextUiStringKey="readmin.surface.fields.projection_interpolation"
      />
      {projectionShape === "sphere" && (
        <>
          <FormDialogButtons>
            <Button
              variant="outlined"
              disabled={sphereCameraConfigPresent}
              onClick={() => {
                fovHorizontalDegreesSet(180);
                fovVerticalDegreesSet(90);
                pitchDegreesSet(-40);
                rollDegreesSet(0);
                sphereCameraPitchDegreesSet(-25);
              }}
            >
              Initialise Config for 180 Camera
            </Button>
            <Button
              variant="outlined"
              disabled={sphereCameraConfigPresent}
              onClick={() => {
                fovHorizontalDegreesSet(130);
                fovVerticalDegreesSet(90);
                pitchDegreesSet(-20);
                rollDegreesSet(0);
                sphereCameraPitchDegreesSet(-20);
              }}
            >
              Initialise Config for Dome Camera
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                fovHorizontalDegreesSet(null);
                fovVerticalDegreesSet(null);
                pitchDegreesSet(null);
                rollDegreesSet(null);
                sphereCameraPitchDegreesSet(null);
                sphereCameraFovHorizontalDegreesSet(null);
              }}
            >
              Clear Values
            </Button>
          </FormDialogButtons>
          <FormDialogColumns>
            <FormDialogColumn span={2}>
              {!sphereCameraConfigPresent && (
                <Message content="Spherical camera config not present. Please fill in the fields below to enable spherical camera preview." />
              )}
              {sphereCameraConfigPresent && (
                <SphereCameraPreview
                  motioncropPreviewUrl={filterChainPreviewUrl}
                  fovHorizontalDegrees={fovHorizontalDegrees}
                  fovVerticalDegrees={fovVerticalDegrees}
                  pitchDegrees={pitchDegrees}
                  rollDegrees={rollDegrees}
                  sphereCameraPitchDegrees={sphereCameraPitchDegrees}
                  sphereCameraFovHorizontalDegrees={sphereCameraFovHorizontalDegrees}
                />
              )}
            </FormDialogColumn>
            <FormDialogColumn>
              <Field
                name="fovHorizontalDegrees"
                label="FOV Horizontal"
                value={fovHorizontalDegrees}
                onChange={fovHorizontalDegreesSet}
                required
                input={<NumberInput min={0} max={360} unit="degrees" />}
                helperTextUiStringKey="readmin.surface.fields.fov_horizontal"
              />
              <Field
                name="fovVerticalDegrees"
                label="FOV Vertical"
                value={fovVerticalDegrees}
                onChange={fovVerticalDegreesSet}
                required
                input={<NumberInput min={0} max={360} unit="degrees" />}
                helperTextUiStringKey="readmin.surface.fields.fov_vertical"
              />
              <Field
                name="pitchDegrees"
                label="Pitch"
                value={pitchDegrees}
                onChange={pitchDegreesSet}
                required
                input={<NumberInput min={-90} max={90} step={0.01} unit="degrees" />}
                helperTextUiStringKey="readmin.surface.fields.pitch_degrees"
              />
              <Field
                name="rollDegrees"
                label="Roll"
                value={rollDegrees}
                onChange={rollDegreesSet}
                required
                input={<NumberInput min={-90} max={90} step={0.01} unit="degrees" />}
                helperTextUiStringKey="readmin.surface.fields.roll_degrees"
              />
              <Field
                name="sphereCameraPitchDegrees"
                label="Sphere Camera Pitch"
                value={sphereCameraPitchDegrees}
                onChange={sphereCameraPitchDegreesSet}
                required
                input={<NumberInput min={-90} max={90} step={0.01} unit="degrees" />}
                helperTextUiStringKey="readmin.surface.fields.sphere_camera_pitch_degrees"
              />
              <Field
                name="sphereCameraFovHorizontalDegrees"
                label="Sphere Camera FOV Horizontal"
                value={sphereCameraFovHorizontalDegrees}
                onChange={sphereCameraFovHorizontalDegreesSet}
                input={<NumberInput min={0} max={90} unit="degrees" initValue={50} />}
                helperTextUiStringKey="readmin.surface.fields.sphere_camera_fov_horizontal_degrees"
              />
            </FormDialogColumn>
          </FormDialogColumns>
        </>
      )}
      <Field
        name="courtCorners"
        label="Court Corners"
        defaultValue={data?.surface.courtCorners}
        input={<PolygonInput backgroundUrl={filterChainPreviewUrl} />}
        helperTextUiStringKey="readmin.surface.fields.court_corners"
      />
      <Field
        name="maxZoom"
        label="Max Zoom"
        defaultValue={data?.surface.maxZoom}
        input={<NumberInput step={0.1} min={0} max={5} />}
        helperTextUiStringKey="readmin.surface.fields.max_zoom"
      />
      <Field
        name="macroFrameVerticalBalance"
        label="Macro Frame Vertical Balance"
        defaultValue={data?.surface.macroFrameVerticalBalance}
        input={<NumberInput step={0.1} min={0} max={1} />}
        helperTextUiStringKey="readmin.surface.fields.macro_frame_vertical_balance"
      />
      <FormSection header="Advertising">
        <Field
          name="advertisingBillboardHeight"
          label="Advertising Billboard Height"
          defaultValue={data?.surface.advertisingBillboardHeight}
          input={<NumberInput unit="pixels" min={0} max={120} initValue={80} />}
          helperTextUiStringKey="readmin.surface.fields.advertising_billboard_height"
        />
        <Field
          name="advertisingBillboardAngle"
          label="Advertising Billboard Angle"
          defaultValue={data?.surface.advertisingBillboardAngle}
          input={<NumberInput unit="??" step={0.01} min={0} max={1} />}
          helperTextUiStringKey="readmin.surface.fields.advertising_billboard_angle"
        />
        <Field
          name="advertisingBillboardScrollSpeed"
          label="Advertising Billboard Scroll Speed"
          defaultValue={data?.surface.advertisingBillboardScrollSpeed}
          input={<NumberInput unit="??" step={0.01} min={0} max={1} />}
          helperTextUiStringKey="readmin.surface.fields.advertising_billboard_scroll_speed"
        />
        <Field
          name="advertisingBillboardAlwaysForeground"
          label="Advertising Billboard Always Foreground"
          defaultValue={data?.surface.advertisingBillboardAlwaysForeground}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.surface.fields.advertising_billboard_always_foreground"
        />
        <Field
          name="advertisingBillboardBottomEdge"
          label="Advertising Billboard Bottom Edge"
          defaultValue={data?.surface.advertisingBillboardBottomEdge}
          input={<PolygonInput backgroundUrl={filterChainPreviewUrl} stroke />}
          helperTextUiStringKey="readmin.surface.fields.advertising_billboard_bottom_edge"
        />
        <Field
          name="advertisingCentreCourt"
          label="Advertising Centre Court"
          defaultValue={data?.surface.advertisingCentreCourt}
          input={<PolygonInput backgroundUrl={filterChainPreviewUrl} stroke fill />}
          helperTextUiStringKey="readmin.surface.fields.advertising_centre_court"
        />
      </FormSection>
      <Field
        name="netLeftPolygon"
        label="Net Left Polygon"
        defaultValue={data?.surface.netLeftPolygon}
        input={<PolygonInput backgroundUrl={filterChainPreviewUrl} stroke fill />}
        helperTextUiStringKey="readmin.surface.fields.net_left_polygon"
      />
      <Field
        name="netRightPolygon"
        label="Net Right Polygon"
        defaultValue={data?.surface.netRightPolygon}
        input={<PolygonInput backgroundUrl={filterChainPreviewUrl} stroke fill />}
        helperTextUiStringKey="readmin.surface.fields.net_right_polygon"
      />
      <Field
        name="courtLength"
        label="Court Length"
        defaultValue={data?.surface.courtLength}
        input={<NumberInput unit="metres" step={0.01} min={5} max={40} />}
        helperTextUiStringKey="readmin.surface.fields.court_length"
      />
      <Field
        name="courtWidth"
        label="Court Width"
        defaultValue={data?.surface.courtWidth}
        input={<NumberInput unit="metres" step={0.01} min={5} max={20} />}
        helperTextUiStringKey="readmin.surface.fields.court_width"
      />
      <Field
        name="cameraHeight"
        label="Camera Height"
        defaultValue={data?.surface.cameraHeight}
        input={<NumberInput unit="metres" step={0.01} />}
        helperTextUiStringKey="readmin.surface.fields.camera_height"
      />
      <Field
        name="cameraDistance"
        label="Camera Distance"
        defaultValue={data?.surface.cameraDistance}
        input={<NumberInput unit="metres" step={0.01} />}
        helperTextUiStringKey="readmin.surface.fields.camera_distance"
      />
      <Field
        label="Preview Using Video"
        value={video}
        onChange={videoSet}
        input={
          <ReadminObjectInput optionsTypename="Video" optionsFilters={{ for_camera_id: data?.surface.camera?.id }} />
        }
        helperTextUiStringKey="readmin.surface.fields.preview_using_video"
      />
    </FormDialog>
  );
}
